/*
 * <vitesse-widget-host> — one widget, isolated, on a leash.
 *
 * This element owns the trust boundary. The widget it hosts runs in an
 * opaque-origin frame with no credentials, no storage and no network, and every
 * capability it has arrives through this element, which checks each one against
 * a CapabilityGrant. That is what lets the same component serve a signed-in
 * operator and a read-only shared link without a second rendering path: the
 * grant differs, nothing else does.
 *
 * It is also the only place that knows about the backend. The frame cannot
 * reach it, the widget does not know it exists, and a widget that asks for a
 * symbol outside the grant simply never receives values — it is not told which
 * ones were dropped, because an enumeration oracle would let a shared link map
 * the plant it was not given.
 */

import { LitElement, html, css, type PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { keyed } from 'lit/directives/keyed.js';
import { FRAME_SANDBOX, frameDoc } from './runtime/frame-doc';
import { FRAME_JS } from 'virtual:widget-frame';
import type { ModelManager } from '../main/managers/model-manager';
import type { HostToWidget, WidgetToHost } from './runtime/protocol';
import {
  type CapabilityGrant, DENY_ALL, isExpired, mayCommand, writeRoute,
} from './runtime/capability';
import { subscribeValues } from './value-fanout';
import { CATALOG } from './catalog';
import type { WidgetMeta } from './runtime/sdk';
import { splitSourceId } from './runtime/source-id';
import { recordValues } from './board-metrics';
import { getSession } from '@vitesse/base-ui';
import '@vitesse/base-ui/elements/vitesse-gate-overlay';
import { watchTheme } from './theme-watch';
import { planSubscriptions, grantedSets, filterValues, declaredReads, MAX_INFLIGHT_REQUESTS } from './runtime/enforce';
import type { BackendClient } from '../main/bridge/backend-client';
import type { StreamFrame } from '../core/bridge/types';
import type { TypedValue } from '../core/bridge/connect-client';

/**
 * The palette a widget may use, resolved from the app's own computed style and
 * pushed into the frame. A widget cannot name a token that is not on this list,
 * so the list IS the design system as far as widgets are concerned — and it is
 * read from the live theme rather than restated, so it can never disagree with
 * the app.
 */
const EXPOSED_TOKENS = [
  '--text-primary', '--text-secondary', '--card-bg', '--bg-primary', '--bg-secondary',
  '--bg-tertiary', '--border-color', '--border-color-light', '--surface-raised',
  '--blue', '--green', '--yellow', '--amber', '--red', '--purple', '--cyan', '--gray',
  '--on-accent',
  // The base chart's axis palette, so a widget's grid IS the chart's grid.
  '--chart-grid', '--chart-axis', '--chart-axis-label',
  // The readout chip's own tokens.
  '--scrim-color', '--text-tertiary',
] as const;

/** How far outside the viewport a tile still counts as worth painting. */
const VIEWPORT_MARGIN = '300px';

/*
 * WATCHDOGS.
 *
 * A widget runs in a worker, so it cannot hang the app - but it can absolutely
 * hang ITSELF, and an unwatched hung widget is a blank tile that never explains
 * why. On a wall display nobody is there to notice, which makes "silently
 * blank" the worst outcome available: an operator reads a stale-looking tile as
 * data rather than as a failure.
 *
 * Two different silences, two different deadlines. Neither can be a single
 * timer, because they mean opposite things about whether the widget ever ran.
 */

/** No `ready` after init: setup() threw into a loop, or the source never compiled. */
const LOAD_TIMEOUT_MS = 8_000;

/**
 * Structural equality over the small, JSON-shaped values this element takes as
 * inputs. Cheap because grants and configs are both bounded and flat.
 */
function same(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  try { return JSON.stringify(a) === JSON.stringify(b); } catch { return false; }
}

/**
 * Ready, running, on screen, visible - and no heartbeat. The widget's frame loop
 * has stopped. Generous relative to the ~1s beat because a slow machine under a
 * hundred tiles is not a fault, and a false fault is worse than a late one.
 */
const LIVENESS_TIMEOUT_MS = 15_000;
/** How long a silent frame has to answer a ping before it is declared dead. */
const PROBE_TIMEOUT_MS = 4_000;
const RESTART_BASE_MS = 2_000;
const RESTART_LIMIT = 6;
const HEALTHY_RESET_MS = 60_000;

@customElement('vitesse-widget-host')
export class VitesseWidgetHost extends LitElement {
  static styles = css`
    :host { display: block; width: 100%; height: 100%; position: relative; }
    iframe { width: 100%; height: 100%; border: 0; display: block; background: transparent; }
    .fault {
      position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;
      font: 11px ui-monospace, monospace; color: var(--text-secondary);
      background: var(--card-bg); text-align: center; padding: 8px;
    }
  `;

  /** Widget module source. Changing it remounts the frame from scratch. */
  @property({ attribute: false }) source = '';
  /** Author/user config handed to the widget verbatim. */
  @property({ attribute: false }) config: Record<string, unknown> = {};
  /** What this rendering context may do. Defaults to nothing. */
  @property({ attribute: false }) grant: CapabilityGrant = DENY_ALL;
  /** The data plane. Without one the widget still renders, with no values. */
  @property({ attribute: false }) backend?: BackendClient;

  /**
   * A STATIC picture instead of a live one. When set, the widget's subscribe
   * request is answered with these values once and no stream is opened — the
   * marketplace card shows a dial at 63 rather than an empty face. The grant is
   * still applied to everything else; a preview cannot write.
   */
  @property({ attribute: false }) previewValues?: TypedValue[];
  /** Made-up history for a preview, replayed through the widget before the live readings so its windows start full. */
  @property({ attribute: false }) previewHistory?: TypedValue[];
  /** Which gateway this tile's symbols belong to. */
  @property({ type: String }) gatewayId = '';
  /** The board this tile belongs to — the key its metrics are recorded under. */
  @property({ type: String }) boardKey = '';
  /** The raw fleet roster / cloud servers / org names — consumed only by the
   *  native map. Sandboxed widgets never see them: they are bound to the native
   *  element directly and no protocol message carries them. */
  @property({ attribute: false }) mapFleet: unknown[] = [];
  @property({ attribute: false }) mapServers: unknown[] = [];
  @property({ attribute: false }) orgNames: Map<string, string> = new Map();
  /** The model manager — consumed only by the native model tiles. */
  @property({ attribute: false }) models?: ModelManager;

  @state() private faultMessage = '';

  private port?: MessagePort;
  /** One per shared subscription this tile has joined. */
  private releases: (() => void)[] = [];
  private unwatchTheme?: () => void;
  private io?: IntersectionObserver;
  private ro?: ResizeObserver;
  private visible = true;
  private onScreen = true;
  private expiryTimer = 0;
  private loadTimer = 0;
  /**
   * Identity of the current frame. Bumping it makes Lit build a NEW iframe
   * rather than reuse the cached one - which is the only way to get a second
   * frame to speak, since the same element with the same srcdoc does not reload.
   *
   * A nonce rather than an on/off flag, because "off then on across two paints"
   * is a two-step that can interleave: two mounts overlapping could leave the
   * flag off, and a host with no frame at all has nothing to recover it.
   * Observed as twenty-three tiles - exactly the on-screen ones, which are the
   * ones the grid moves - sitting blank with no iframe in their shadow root. A
   * single assignment cannot half-happen.
   */
  @state() private frameNonce = 0;
  private livenessTimer = 0;
  /** True once the widget has reported ready; gates which watchdog applies. */
  private started = false;
  /** Encoded symbol ids this widget is actually receiving, per controller. */
  private granted = new Map<string, Set<string>>();
  /**
   * Write/command requests awaiting a backend answer. Bounded: a widget that
   * fires requests faster than they settle would otherwise queue unbounded work
   * on the host, which needs no sandbox escape to be a denial of service.
   */
  private inflight = 0;

  /* ------------------------------------------------------------ lifecycle -- */

  connectedCallback(): void {
    super.connectedCallback();
    this.unwatchTheme = watchTheme(() => this.refreshTheme());
    document.addEventListener('visibilitychange', this.onVisibility);
    window.addEventListener('message', this.onFrameHello);
    this.armExpiry();
    // Reconnected after a move: teardown took the port with it, so the tile is
    // mounted but mute. `updated()` will not fire - no property changed - so
    // the remount has to happen here.
    if (this.hasUpdated && !this.port && this.source && !this.isNative) void this.mount();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.unwatchTheme?.();
    this.unwatchTheme = undefined;
    document.removeEventListener('visibilitychange', this.onVisibility);
    window.removeEventListener('message', this.onFrameHello);
    clearTimeout(this.restartTimer);
    this.teardown();
  }

  protected updated(changed: PropertyValues<this>): void {
    // Source, config and grant are all mount-time inputs: a widget cannot be
    // handed a wider grant mid-flight, because it may already have read values
    // under the old one and there is no way to un-tell it. A change remounts.
    // `backend` and `gatewayId` are in this list because property order is not
    // guaranteed: a host whose source lands before its backend would subscribe
    // to nothing and never retry, leaving a permanently empty tile with no
    // error anywhere. Remounting is the honest response - the widget has
    // already declared against a world that has changed underneath it.
    // Compared by VALUE, not identity. A caller that rebuilds an identical
    // grant or config object - the natural thing to write inside a render -
    // would otherwise restart this widget on every paint, and the tile would
    // spend its whole life mounting. What must remount is a change in what the
    // widget is allowed to do, and that is a question about contents.
    // A preview that keeps ticking: the marketplace card feeds fresh made-up readings so a
    // sparkline or a spectrum shows itself at work. Not a remount - the same widget, more values.
    if (changed.has('previewValues') && !changed.has('source') && this.previewValues && this.started) {
      this.send({ kind: 'values', values: this.previewValues });
    }
    if (changed.has('source') && this.isNative && this.previewValues === undefined) {
      this.dispatchEvent(new CustomEvent('widget-ready', { detail: this.nativeMeta(), bubbles: true, composed: true }));
    }
    const remount =
      changed.has('source') || changed.has('backend') || changed.has('gatewayId') ||
      (changed.has('grant') && !same(changed.get('grant'), this.grant)) ||
      (changed.has('config') && !same(changed.get('config'), this.config));
    if (remount) {
      this.teardown();
      this.faultMessage = '';
      this.armExpiry();
      if (this.source && !this.isNative) void this.mount();
    }
    // The native map's "values" are fleet roster frames (~1 Hz). Feed them to the
    // board meter so a map board is LIVE like any other: rate = roster cadence,
    // lag = how stale the freshest online box's heartbeat is — an honest "how far
    // behind the fleet am I looking". Without this, a map-only board reads as
    // having no data at all, which is exactly wrong.
    if (this.isNative && changed.has('mapFleet') && this.boardKey) {
      // Rate only, no lag claim: roster frames arrive ~1 Hz but their lastSeen
      // stamps move at heartbeat cadence — honest for presence, meaningless as a
      // paint-latency figure (it read as a sawtooth climbing toward a minute).
      recordValues(`${this.gatewayId}/${this.boardKey}`, 1, 0);
    }
  }

  /*
   * NATIVE WIDGETS — the deliberate, narrow exception to the sandbox.
   *
   * A `native:` source is not code: it names one of the app's OWN components
   * (today: the map), rendered directly in the document, because what it draws
   * cannot exist inside the frame — the sandbox has no network for map tiles
   * and no DOM for a GL canvas, and that is the sandbox working as designed.
   * The trust model holds because the component ships with the app and its
   * config carries DATA only (coordinates, labels — never code). The id space
   * is disjoint from everything installable: a marketplace artifact's source
   * is inline widget code by construction, so nothing published can name its
   * way into the document.
   */
  private get isNative(): boolean {
    return this.source.startsWith('native:');
  }

  /**
   * What the base widget's settings dialog offers for a native tile. A sandbox
   * widget declares this in its meta on `ready`; a native tile's declaration
   * lives in the catalog, and the chart keeps its own, richer dialog.
   */
  private nativeMeta(): WidgetMeta {
    // The chart's traces are edited in the base dialog like any list setting; its own dialog
    // (window, theme, thresholds) sits behind a tile-bar action it announces itself.
    if (this.source === 'native:chart') return { settings: [{ key: 'symbols', label: 'Traces', kind: 'list', fields: [
      { key: 'label', label: 'Label', kind: 'text' }, { key: 'controllerId', label: 'Source', kind: 'text' }, { key: 'symbolId', label: 'Symbol', kind: 'text' }, { key: 'unit', label: 'Unit', kind: 'text' },
    ] }] };
    const entry = CATALOG.find((c) => c.source === this.source);
    return { settings: entry?.settings ?? [] };
  }

  /**
   * The tile chrome's gear. Returns true when the widget opened its own dialog
   * (the chart); false means the caller shows the shared one for `meta.settings`.
   */
  /** A tile-bar action button was pressed: the chart runs its own; a sandbox widget hears `action`. */
  runAction(id: string): void {
    if (this.source === 'native:chart') {
      const chart = this.renderRoot.querySelector('vitesse-dashboard-chart') as (HTMLElement & { runAction(id: string): void }) | null;
      chart?.runAction(id);
      return;
    }
    if (!this.isNative) this.send({ kind: 'action', id });
  }

  openSettings(): boolean {
    return false;
  }

  /**
   * The last ten minutes from the historian, replayed through the widget before the live
   * readings, so a window opens full instead of drawing from the moment somebody looked — the
   * same seed the chart tile gives itself. Only controller sources; a failed query leaves the
   * widget live-only, as before.
   */
  private async backfill(byController: Map<string, string[]>): Promise<void> {
    if (!this.backend || !this.gatewayId) return;
    const now = Date.now();
    const out: TypedValue[] = [];
    for (const [sourceId, symbolIds] of byController) {
      if (splitSourceId(sourceId).kind !== 'controller') continue;
      try {
        const samples = await this.backend.api.queryRange(this.gatewayId, sourceId, symbolIds, now - 600_000, now);
        for (const s of samples) out.push({ symbolId: s.symbol, value: s.num, num: s.num, tsMs: s.tsMs });
      } catch { /* live-only */ }
    }
    if (out.length && this.started) this.send({ kind: 'values', values: out, replay: true });
  }

  /*
   * Native widgets render in LIGHT DOM. The shadow root is the sandbox's
   * shell — nothing outside may style the frame, and the frame needs nothing
   * from outside. A native component is the opposite case: maplibre's global
   * stylesheet and the app's token SCSS MUST reach it, and inside the shadow
   * the map mounted a working canvas that no stylesheet could position —
   * present in the DOM, invisible on screen. `source` is bound in the same
   * template that creates this element, so it is committed before Lit asks
   * for the render root; a host is one kind or the other for its lifetime
   * (the grid keys hosts by tile id, so a tile changing kind is a new host).
   */
  protected createRenderRoot(): HTMLElement | DocumentFragment {
    if (this.isNative) {
      // Light DOM (so the app's token SCSS + the map's own stylesheet reach the
      // native element). Static `:host` styles do NOT apply to a light-DOM host,
      // so set the box here: a native widget fills its tile, and the fleet map's
      // content is position:absolute — it needs this positioned, sized ancestor
      // or it collapses to height 0.
      this.style.position = 'relative';
      this.style.display = 'block';
      this.style.height = '100%';
      return this;
    }
    return super.createRenderRoot();
  }

  render() {
    if (this.source === 'native:chart') {
      // The app's own chart, bound through the page fanout; the grant is checked inside.
      void import('./dashboard-chart');
      return html`<vitesse-dashboard-chart
        .backend=${this.backend}
        .gatewayId=${this.gatewayId}
        .boardKey=${this.boardKey}
        .grant=${this.grant}
        .config=${this.config}
        .preview=${this.previewValues !== undefined}
      ></vitesse-dashboard-chart>`;
    }
    if (this.source === 'native:alarms') {
      // The app's own list rows, bound through the page fanout; the grant is checked inside.
      void import('./dashboard-alarms');
      return html`<vitesse-dashboard-alarms
        .backend=${this.backend}
        .gatewayId=${this.gatewayId}
        .boardKey=${this.boardKey}
        .grant=${this.grant}
        .config=${this.config}
        .preview=${this.previewValues !== undefined}
      ></vitesse-dashboard-alarms>`;
    }
    if (this.source === 'native:note') {
      void import('./dashboard-note');
      return html`<vitesse-dashboard-note .config=${this.config} .preview=${this.previewValues !== undefined}></vitesse-dashboard-note>`;
    }
    if (this.source === 'native:model-commands' || this.source === 'native:model-statechart') {
      void import('./dashboard-model');
      return html`<vitesse-dashboard-model
        .kind=${this.source === 'native:model-commands' ? 'commands' : 'statechart'}
        .config=${this.config}
        .models=${this.models}
        .preview=${this.previewValues !== undefined}
      ></vitesse-dashboard-model>`;
    }
    if (this.source === 'native:map') {
      /*
       * Lazily defined: maplibre is ~250 KB of dependency a board without a
       * map must never pay for. The import races the first render — Lit
       * renders the (unknown) element immediately and upgrades it when the
       * definition lands, which is custom elements working as designed.
       */
      // The fleet map is an ultra surface (fleet_manager:full) — the same gate
      // the fleet app's Map view carries, so the dashboard tile and the fleet
      // page agree. A denied board sees the tile's shape, and crucially never
      // pays the maplibre import.
      const mapAllowed = getSession()?.allows({ capability: 'fleet_manager', level: 'full' }) !== false;
      if (mapAllowed) void import('./dashboard-map');
      return html`<vitesse-gate-overlay
        style="position:absolute; inset:0;"
        capability="fleet_manager"
        level="full"
        context="dashboard/map"
        .content=${mapAllowed
          ? html`<vitesse-dashboard-map .config=${this.config} .fleet=${this.mapFleet} .cloudServers=${this.mapServers} .orgNames=${this.orgNames}></vitesse-dashboard-map>`
          : html``}
      ></vitesse-gate-overlay>`;
    }
    return html`
      ${this.faultMessage
        ? html`<div class="fault" title=${this.faultMessage}>widget stopped</div>`
        : keyed(this.frameNonce, html`<iframe
            part="frame"
            sandbox=${FRAME_SANDBOX}
            .srcdoc=${frameDoc(FRAME_JS)}
          ></iframe>`)}
    `;
  }

  /* ---------------------------------------------------------------- mount -- */

  private async mount(): Promise<void> {
    // A frame that never says hello is the failure this timer exists for.
    // Arming it only once the frame HAS spoken would leave the one case that
    // actually stays silent - a frame that never runs, or whose hello was
    // missed - blank forever, with nothing anywhere saying so. Silence is the
    // symptom, so silence has to be what starts the clock.
    clearTimeout(this.loadTimer);
    this.loadTimer = window.setTimeout(() => this.fault('widget did not start'), LOAD_TIMEOUT_MS);

    // Re-create the frame rather than reuse whatever is in the shadow root. A
    // host that was moved in the DOM - which a grid does routinely while it
    // lays out - is disconnected and reconnected, and teardown closed its port
    // on the way out. The existing iframe is already loaded, so it will never
    // post a second hello, and the tile would stay dark permanently. A new
    // nonce is a new element, which is a frame that speaks.
    this.frameNonce++;
    await this.updateComplete;
    this.observe();
  }

  /**
   * The frame announces itself when its script runs; we answer by transferring
   * a port to whichever window said hello, matched against OUR iframe.
   *
   * Deliberately not driven by the iframe's `load` event: an iframe appended
   * after its srcdoc is set loads about:blank FIRST and fires `load` for it, so
   * a port sent then lands in a blank document with no listener while the real
   * frame comes up holding nothing. Nothing throws and both sides wait forever.
   * Matching on ev.source removes the race rather than narrowing it.
   */
  private onFrameHello = (ev: MessageEvent): void => {
    const frame = this.renderRoot.querySelector('iframe');
    if (!frame || ev.source !== frame.contentWindow) return;
    if ((ev.data as { kind?: string } | null)?.kind !== 'frame-hello') return;

    // A SECOND hello from the same iframe means that frame reloaded, so the
    // port we hold is attached to a document that no longer exists. Ignoring it
    // would leave a permanently silent tile; re-pairing is the only response
    // that recovers. Value streams are torn down with it, because the widget
    // that declared them is gone and the new one has not asked yet.
    if (this.port) {
      this.port.close();
      this.port = undefined;
      for (const r of this.releases) { try { r(); } catch { /* gone */ } }
      this.releases = [];
      this.granted.clear();
    }

    const channel = new MessageChannel();
    this.port = channel.port1;
    this.port.onmessage = (e: MessageEvent<WidgetToHost | { kind: 'frame-ready' }>) =>
      this.onWidgetMessage(e.data);
    this.port.start();

    // Transferring the port IS the grant of a channel. Only this element ever
    // had one to give, which is what makes possession sufficient proof.
    frame.contentWindow?.postMessage('handshake', '*', [channel.port2]);
  };

  private send(m: HostToWidget): void {
    this.port?.postMessage(m);
  }

  /* -------------------------------------------------------------- messages -- */

  private onWidgetMessage(m: WidgetToHost | { kind: 'frame-ready' }): void {
    switch (m.kind) {
      case 'frame-ready': {
        const box = this.getBoundingClientRect();
        this.send({
          kind: 'init',
          source: this.source,
          config: this.config,
          width: Math.round(box.width),
          height: Math.round(box.height),
          tokens: this.resolveTokens(),
          theme: document.documentElement.getAttribute('data-theme') ?? 'dark',
          // A widget that knows it is read-only can render its controls as
          // disabled rather than letting an operator press a button that will
          // silently do nothing. The grant is still what actually refuses.
          readOnly: this.grant.writes.length === 0 && this.grant.commands.length === 0,
        });
        // From here the widget owes us a `ready`. If setup() never returns -
        // an infinite loop, a source that cannot compile - nothing else will
        // ever report it, because the worker is the thing that is stuck.
        clearTimeout(this.loadTimer);
        this.loadTimer = window.setTimeout(
          () => this.fault('widget did not start'), LOAD_TIMEOUT_MS);
        break;
      }

      case 'subscribe':
        if (this.previewValues) {
          if (this.previewHistory?.length) this.send({ kind: 'values', values: this.previewHistory, replay: true });
          // A preview feeds a reading to EVERY source the widget declared, not only the one
          // config.symbolId names. A widget that overlays several symbols, or reads a model's outputs
          // via a `model:<id>` source, would otherwise get a value for that one symbol and NO_READING
          // for the rest — so it draws nothing, and its author (a person or the assistant) cannot tell
          // a broken widget from a preview that never fed it. This is exactly what sent a widget author
          // in circles: the check reported "drew nothing" for a correct multi-source widget. Explicit
          // previewValues still win, so a curated marketplace card is unchanged; a single-source
          // catalog widget declares only config.symbolId, which is already supplied, so nothing is added.
          const supplied = new Set(this.previewValues.map((v) => v.symbolId));
          const synthetic: TypedValue[] = m.symbols
            .filter((s) => !supplied.has(s.symbolId))
            .map((s) => ({ symbolId: s.symbolId, value: 42.5, num: 42.5, tsMs: Date.now() }));
          this.send({ kind: 'values', values: synthetic.length ? [...synthetic, ...this.previewValues] : this.previewValues });
          break;
        }
        void this.openValueStreams(m.symbols);
        break;

      case 'text':
        // Published on the HOST element, where the screen reader walks. The frame's own DOM is
        // unreachable from outside - an opaque origin is the point - so without this a dashboard
        // answers "what is on my screen" with a set of empty titled boxes, which reads as
        // complete and is not. Data, like every other thing on a page.
        this.setAttribute('data-screen-text', m.text);
        break;

      case 'actions':
        this.dispatchEvent(new CustomEvent('tile-actions', { detail: m.actions, bubbles: true, composed: true }));
        break;

      case 'hover':
        // Re-emitted as a DOM event so the tile chrome around this host (which
        // never sees the pointer while it is in the frame) can react.
        this.dispatchEvent(new CustomEvent('tile-hover', { detail: m.in, bubbles: true, composed: true }));
        break;

      case 'ready':
        clearTimeout(this.loadTimer);
        this.started = true;
        this.armLiveness();
        this.dispatchEvent(new CustomEvent('widget-ready', { detail: m.meta, bubbles: true, composed: true }));
        break;

      case 'write': {
        if (this.inflight >= MAX_INFLIGHT_REQUESTS) {
          this.send({ kind: 'reply', id: m.id, ok: false, error: 'too many requests in flight' });
          return;
        }
        // One call resolves both questions: may this happen, and who carries it out.
        const route = writeRoute(this.grant, Date.now(), this.gatewayId, m.controllerId, m.symbolId);
        if (!route || !this.backend) {
          this.send({ kind: 'reply', id: m.id, ok: false, error: 'not permitted' });
          return;
        }
        // Widgets name a symbol the same way everything else in this layer does -
        // by its decoded path - so there is nothing to translate here.
        const write = {
          path: m.symbolId,
          value: m.value,
          dataType: typeof m.value === 'boolean' ? 'boolean'
            : typeof m.value === 'number' ? 'number' : 'string',
        };
        this.inflight++;
        void this.backend.api
          .writeSymbols(this.gatewayId, route.pluginId, m.controllerId, [write])
          .then((results) => {
            this.inflight--;
            // A single write is a one-element batch; its outcome is the batch's.
            const r = results?.[0];
            this.send({ kind: 'reply', id: m.id, ok: !!r?.ok, error: r?.error || undefined });
          })
          .catch((err: unknown) => {
            this.inflight--;
            this.send({ kind: 'reply', id: m.id, ok: false, error: String(err) });
          });
        break;
      }

      case 'command': {
        if (this.inflight >= MAX_INFLIGHT_REQUESTS) {
          this.send({ kind: 'reply', id: m.id, ok: false, error: 'too many requests in flight' });
          return;
        }
        if (!mayCommand(this.grant, Date.now(), this.gatewayId, m.modelId, m.command) || !this.backend) {
          this.send({ kind: 'reply', id: m.id, ok: false, error: 'not permitted' });
          return;
        }
        // executeModelCommand resolves with nothing and throws on failure, so
        // reaching .then() IS the success signal.
        const args: Record<string, string> = {};
        for (const [k, v] of Object.entries(m.args ?? {})) args[k] = String(v);
        this.inflight++;
        void this.backend.api
          .executeModelCommand(this.gatewayId, m.modelId, m.command, args)
          .then(() => { this.inflight--; this.send({ kind: 'reply', id: m.id, ok: true }); })
          .catch((err: unknown) => {
            this.inflight--;
            this.send({ kind: 'reply', id: m.id, ok: false, error: String(err) });
          });
        break;
      }

      case 'heartbeat':
      case 'pong':
        this.armLiveness();
        this.healthySince ||= Date.now();
        break;

      case 'problems':
        // For whoever is writing the widget (the preview shows them); a board just keeps painting.
        this.dispatchEvent(new CustomEvent('widget-problems', { detail: m.problems, bubbles: true, composed: true }));
        break;

      case 'fault':
        this.fault(m.message);
        break;
    }
  }

  /* ---------------------------------------------------------------- values -- */

  /**
   * Open one value stream per controller, carrying ONLY the symbols the grant
   * permits. The filter happens here rather than in the frame: a widget must
   * not be able to reason about what it was denied, and the backend should not
   * be asked for data this context has no right to.
   */
  private async openValueStreams(wanted: { controllerId: string; symbolId: string }[]): Promise<void> {
    if (!this.backend || !this.gatewayId) return;

    const byController = planSubscriptions(this.grant, Date.now(), this.gatewayId, wanted);
    this.granted = grantedSets(byController);

    // Joins the page's shared stream for this controller rather than opening
    // one of its own. Sixty tiles on one controller are one subscription, not
    // sixty - see value-fanout. The grant still decides what this tile may ask
    // for, and onValueFrame still filters what it is handed.
    void this.backfill(byController);
    for (const [sourceId, symbolIds] of byController) {
      /*
       * The prefix picks the STREAM; everything before and after this line
       * treats the id as an opaque string. Model and integration sources ride
       * subscribeSourceValues — the same transport the Sources tab uses, whose
       * worker-side registry key is the source id, so N tiles on one model
       * still share one upstream. Controllers keep the page fanout unchanged.
       */
      const src = splitSourceId(sourceId);
      const release =
        src.kind === 'controller'
          ? subscribeValues(
              this.backend, this.gatewayId, src.id, symbolIds,
              (frame) => this.onValueFrame(sourceId, frame),
            )
          : this.subscribeNonController(src.kind, src.id, sourceId, symbolIds);
      // A remount between the request and this point would otherwise leave a
      // live subscription attached to a widget that no longer exists.
      if (!this.port) { release(); return; }
      this.releases.push(release);
    }
  }

  /*
   * A model's or integration's values, as the same StreamFrame shape the fanout
   * delivers, so onValueFrame cannot tell the kinds apart — the grant filter
   * and the send path stay one code path for all three.
   */
  private subscribeNonController(kind: 'model' | 'integration', id: string, sourceId: string, symbolIds: string[]): () => void {
    // The Comlink call resolves to a transferred port; a remount before it
    // resolves must still tear the stream down, so the release closes over the
    // promise rather than the port.
    let closed = false;
    const portP = Promise.resolve(this.backend!.api.subscribeSourceValues(this.gatewayId, kind, id, symbolIds));
    void portP.then((port) => {
      if (closed) {
        port.postMessage({ kind: 'close' });
        port.close();
        return;
      }
      port.onmessage = (e: MessageEvent<StreamFrame<TypedValue>>) => this.onValueFrame(sourceId, e.data);
      port.start();
    });
    return () => {
      closed = true;
      void portP.then((port) => {
        port.postMessage({ kind: 'close' });
        port.close();
      });
    };
  }

  private onValueFrame(controllerId: string, frame: StreamFrame<TypedValue>): void {
    if (isExpired(this.grant, Date.now())) { this.fault('share expired'); return; }
    const items: TypedValue[] =
      frame.kind === 'snapshot' ? frame.items
      : frame.kind === 'delta' && frame.op === 'upsert' ? [frame.item]
      : [];
    const permitted = filterValues(this.granted, controllerId, items);
    if (permitted.length) {
      /*
       * The board meter — recorded HERE because this is the one line every
       * value crosses on its way to a tile, whatever kind of source produced
       * it. What's measured is deliveries to tiles (two tiles reading one
       * symbol count twice), which is the number the person at the screen is
       * actually experiencing.
       */
      let newest = 0;
      for (const v of permitted) if (v.tsMs > newest) newest = v.tsMs;
      recordValues(`${this.gatewayId}/${this.boardKey}`, permitted.length, newest);
      this.send({ kind: 'values', values: permitted });
    }
  }

  /* ------------------------------------------------------- visibility/size -- */

  private observe(): void {
    this.io?.disconnect();
    this.io = new IntersectionObserver(
      (entries) => {
        this.onScreen = entries.some((e) => e.isIntersecting);
        this.syncRunning();
      },
      { rootMargin: VIEWPORT_MARGIN },
    );
    this.io.observe(this);

    this.ro?.disconnect();
    this.ro = new ResizeObserver(() => {
      const b = this.getBoundingClientRect();
      this.send({ kind: 'resize', width: Math.round(b.width), height: Math.round(b.height) });
    });
    this.ro.observe(this);
  }

  private onVisibility = (): void => {
    this.visible = document.visibilityState === 'visible';
    this.syncRunning();
  };

  /**
   * Restart the liveness deadline - but only while the widget is actually
   * expected to be beating.
   *
   * A paused tile is SUPPOSED to be silent, and a hidden tab suspends the very
   * animation frame the heartbeat rides on. Faulting on either would turn
   * correct behaviour into an error, and would do it precisely when nobody is
   * looking to see it was wrong.
   */
  private armLiveness(): void {
    clearTimeout(this.livenessTimer);
    this.probing = false;
    if (!this.started || !this.visible || !this.onScreen) return;
    this.livenessTimer = window.setTimeout(() => this.probe(), LIVENESS_TIMEOUT_MS);
  }

  /*
   * A MISSED HEARTBEAT IS A QUESTION, NOT A VERDICT.
   *
   * The heartbeat rides a timer inside a cross-origin frame, and the browser
   * throttles exactly that timer whenever it decides nobody can see the frame —
   * a window covered by another app leaves the page "visible" while the frame's
   * timers slow to once a minute. Tiles on a board left behind another window
   * came back marked stopped, every one of them, with nothing wrong. So a silent
   * frame is asked first: a message round trip is not throttled. Only a frame
   * that cannot answer is dead.
   */
  private probing = false;
  private probe(): void {
    if (this.probing || !this.port) return;
    this.probing = true;
    this.send({ kind: 'ping' });
    this.livenessTimer = window.setTimeout(() => this.fault('widget stopped responding'), PROBE_TIMEOUT_MS);
  }

  /**
   * A widget paints only when someone could actually see it. Off-screen tiles
   * and hidden tabs cost nothing — which matters most on a wall display holding
   * a hundred tiles, where the alternative is a hundred idle animation loops.
   */
  private syncRunning(): void {
    this.send({ kind: this.visible && this.onScreen ? 'resume' : 'pause' });
    // A tile going off-screen must not then be faulted for going quiet, and one
    // coming back must be given a fresh deadline rather than the remains of an old.
    this.armLiveness();
  }

  /* ---------------------------------------------------------------- tokens -- */

  private resolveTokens(): Record<string, string> {
    const cs = getComputedStyle(this);
    const out: Record<string, string> = {};
    for (const name of EXPOSED_TOKENS) {
      const v = cs.getPropertyValue(name).trim();
      if (v) out[name] = v;
    }
    return out;
  }

  /**
   * What this tile actually reads, for capture into a shareable snapshot.
   *
   * Taken from the live subscription rather than from config, so a captured
   * snapshot cannot claim more than the host was willing to deliver - which is
   * the property a share's derived grant depends on.
   */
  capturedReads(): { controllerId: string; symbolId: string }[] {
    return declaredReads(new Map([...this.granted].map(([c, set]) => [c, [...set]])));
  }

  /** Re-resolve and push the palette; called when the app's theme flips. */
  refreshTheme(): void {
    this.send({
      kind: 'theme',
      theme: document.documentElement.getAttribute('data-theme') ?? 'dark',
      tokens: this.resolveTokens(),
    });
  }

  /* ---------------------------------------------------------------- expiry -- */

  /**
   * Stop serving at expiry without waiting for a server to refuse. A shared
   * link's window closing is a local fact, and a tile that keeps showing live
   * plant data past it — even briefly, even because a stream was already
   * open — is the whole failure this guards against.
   */
  private armExpiry(): void {
    clearTimeout(this.expiryTimer);
    const at = this.grant.expiresAtMs;
    if (at === null) return;
    const ms = at - Date.now();
    if (ms <= 0) { this.fault('share expired'); return; }
    this.expiryTimer = window.setTimeout(() => this.fault('share expired'), ms);
  }

  /* ------------------------------------------------------------- teardown -- */

  /*
   * A STOPPED TILE TRIES AGAIN. A widget that died — its worker threw, its
   * frame went silent, it never started — is restarted after a pause that
   * doubles each time, up to RESTART_LIMIT attempts; a tile that then ran
   * healthily for a minute earns a fresh count. The page is told on the way
   * down and on the way back, so its "widgets stopped" badge is true at every
   * moment. An expired share is the one fault that is final: nothing about
   * a retry can change what the grant says.
   */
  private restarts = 0;
  private restartTimer = 0;
  private healthySince = 0;
  private fault(message: string): void {
    this.faultMessage = message;
    this.teardown();
    this.dispatchEvent(new CustomEvent('widget-fault', { detail: message, bubbles: true, composed: true }));
    if (message === 'share expired' || this.isNative || !this.source) return;
    if (this.healthySince && Date.now() - this.healthySince > HEALTHY_RESET_MS) this.restarts = 0;
    this.healthySince = 0;
    if (this.restarts >= RESTART_LIMIT) return;
    const delay = RESTART_BASE_MS * 2 ** this.restarts;
    this.restarts++;
    clearTimeout(this.restartTimer);
    this.restartTimer = window.setTimeout(() => {
      if (!this.isConnected || this.port) return;
      this.faultMessage = '';
      this.dispatchEvent(new CustomEvent('widget-recovered', { bubbles: true, composed: true }));
      void this.mount();
    }, delay);
  }

  private teardown(): void {
    // A stopped tile must stop describing itself, or the screen reader keeps
    // reporting the last thing it drew as though it were still on the wall.
    this.removeAttribute('data-screen-text');
    clearTimeout(this.expiryTimer);
    clearTimeout(this.loadTimer);
    clearTimeout(this.livenessTimer);
    this.started = false;
    this.io?.disconnect(); this.io = undefined;
    this.ro?.disconnect(); this.ro = undefined;
    for (const r of this.releases) { try { r(); } catch { /* already gone */ } }
    this.releases = [];
    this.granted.clear();
    this.inflight = 0;
    this.port?.close();
    this.port = undefined;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-widget-host': VitesseWidgetHost;
  }
}
